<script lang="ts">
  import type { Snippet } from 'svelte';
  import ThemeToggle from '../components/ThemeToggle.svelte';
  import ClinicBrandMark from '../components/ClinicBrandMark.svelte';
  import type { BrandMark } from '../lib/branding.svelte';
  import type { NavSection, Tab } from '../lib/navigation';
  import { labelForTab } from '../lib/navigation';
  import { planLabel } from '../lib/tenant.svelte';
  import { TOSCANA_NAME, loadLocalBranding } from '../lib/branding.svelte';

  let {
    sections,
    activeTab,
    tenantName,
    tenantId,
    brandMark,
    planSlug = null,
    roleLabel = null,
    usuario = '',
    onNavigate,
    onLogout,
    children,
  }: {
    sections: NavSection[];
    activeTab: Tab;
    tenantName: string;
    tenantId: string;
    brandMark: BrandMark;
    orgTipo?: string | null;
    planSlug?: string | null;
    roleLabel?: string | null;
    usuario?: string;
    onNavigate: (tab: Tab) => void;
    onLogout: () => void;
    children: Snippet;
  } = $props();

  let mobileOpen = $state(false);
  const pageTitle = $derived(labelForTab(activeTab, sections));
  const plan = $derived(planLabel(planSlug));
  const activeSection = $derived(sections.find(s => s.items.some(i => i.id === activeTab)));
  let local = $state<ReturnType<typeof loadLocalBranding>>(loadLocalBranding());

  function refreshBranding() {
    local = loadLocalBranding();
  }

  $effect(() => {
    refreshBranding();
    window.addEventListener('toscana-branding-updated', refreshBranding);
    return () => window.removeEventListener('toscana-branding-updated', refreshBranding);
  });
</script>

<div class="app-shell">
  <aside class="sidebar" class:open={mobileOpen}>
    <div class="sidebar-brand">
      <ClinicBrandMark mark={brandMark} size="lg" />
      <div class="sidebar-brand-text">
        <strong>{tenantName || TOSCANA_NAME}</strong>
        {#if plan}<span class="sidebar-plan">{plan}</span>{/if}
      </div>
    </div>

    <nav class="sidebar-nav">
      {#key sections.length}
      {#each sections as sec (sec.id)}
        <div class="nav-section">
          <span class="nav-section-label">{sec.label}</span>
          {#each sec.items as item (item.id)}
            <button
              type="button"
              class="nav-item"
              class:active={activeTab === item.id}
              onclick={() => { onNavigate(item.id); mobileOpen = false; }}
            >
              <span class="nav-icon" aria-hidden="true">{item.icon}</span>
              <span class="nav-text">{item.label}</span>
            </button>
          {/each}
        </div>
      {/each}
      {/key}
    </nav>

    <div class="sidebar-footer">
      {#if local.horario}
        <div class="sf-item">🕐 {local.horario}</div>
      {/if}
      {#if local.telefono}
        <div class="sf-item">📞 {local.telefono}</div>
      {/if}
      <div class="sf-brand">Toscana Systems</div>
    </div>
  </aside>

  {#if mobileOpen}
    <button type="button" class="backdrop" aria-label="Cerrar menú" onclick={() => mobileOpen = false}></button>
  {/if}

  <div class="main-col">
    <header class="topbar">
      <button type="button" class="menu-toggle" onclick={() => mobileOpen = !mobileOpen} aria-label="Menú">
        ☰
      </button>
      <div class="topbar-title">
        {#if activeSection}<span class="crumb">{activeSection.label}</span>{/if}
        <h1>{pageTitle}</h1>
      </div>
      <div class="topbar-actions">
        <ThemeToggle />
        <div class="nav-user">
          <div class="user-avatar">
            {#if local.profileImage}
              <img src={local.profileImage} alt="" class="avatar-img" />
            {:else}
              {usuario ? usuario.charAt(0).toUpperCase() : '👤'}
            {/if}
          </div>
          <div class="user-info">
            <span class="user-name">{usuario}</span>
            {#if roleLabel}<span class="user-role">{roleLabel}</span>{/if}
          </div>
          <button class="logout-btn" onclick={onLogout} title="Cerrar sesión">⏻</button>
        </div>
      </div>
    </header>

    <main class="content">
      {@render children()}
    </main>

    <footer class="app-foot">
      <span>© {new Date().getFullYear()} Toscana Systems, S.A.</span>
      <a href="https://www.toscanasystems.com" target="_blank" rel="noopener">www.toscanasystems.com</a>
    </footer>
  </div>
</div>

<style>
  .app-shell {
    display: flex;
    min-height: 100vh;
    background: var(--app-bg, var(--bg-base));
  }

  /* ===== SIDEBAR ===== */
  .sidebar {
    width: 260px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    background: var(--sidebar-bg, #14532d);
    color: var(--sidebar-text, #fff);
    border-right: 1px solid rgba(255,255,255,0.08);
    box-shadow: 0 2px 16px rgba(0,0,0,0.08);
    position: sticky;
    top: 0;
    height: 100vh;
    overflow-y: auto;
    z-index: 40;
  }

  .sidebar-brand {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 1rem 1rem 0.85rem;
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }
  .sidebar-brand-text {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
  }
  .sidebar-brand-text strong {
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--sidebar-text, #fff);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 11rem;
  }
  .sidebar-plan {
    font-size: 0.58rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.12rem 0.5rem;
    border-radius: 999px;
    background: color-mix(in srgb, var(--brand,#15803d) 40%, transparent);
    border: 1px solid rgba(255,255,255,0.14);
    color: var(--sidebar-text, #fff);
    width: fit-content;
  }

  .sidebar-nav { flex: 1; padding: 0.5rem 0.65rem 1rem; overflow-y: auto; }
  .nav-section { margin-bottom: 0.85rem; }
  .nav-section-label {
    display: block;
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: color-mix(in srgb, var(--sidebar-text, #fff) 45%, transparent);
    padding: 0.35rem 0.55rem 0.25rem;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    width: 100%;
    padding: 0.5rem 0.65rem;
    margin-bottom: 0.15rem;
    border: 0;
    border-radius: 0.65rem;
    background: transparent;
    color: color-mix(in srgb, var(--sidebar-text, #fff) 88%, transparent);
    font: inherit;
    font-size: 0.84rem;
    font-weight: 500;
    cursor: pointer;
    text-align: left;
    transition: background 0.18s, color 0.18s;
  }
  .nav-item:hover {
    background: var(--sidebar-hover-bg, rgba(255,255,255,0.1));
    color: var(--sidebar-text, #fff);
  }
  .nav-item.active {
    background: var(--sidebar-active-bg, color-mix(in srgb, var(--brand,#15803d) 55%, transparent));
    color: #fff;
    font-weight: 700;
    box-shadow: 0 4px 16px color-mix(in srgb, var(--brand,#15803d) 30%, transparent);
  }
  .nav-icon { font-size: 1.05rem; width: 1.35rem; text-align: center; flex-shrink: 0; }

  .sidebar-footer {
    padding: 0.75rem 1rem;
    border-top: 1px solid rgba(255,255,255,0.08);
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.62rem;
    color: color-mix(in srgb, var(--sidebar-text, #fff) 45%, transparent);
  }
  .sf-item {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sf-brand {
    font-size: 0.58rem;
    margin-top: 0.3rem;
    opacity: 0.4;
  }

  .main-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /* ===== NAVBAR ===== */
  .topbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.55rem 1.25rem;
    min-height: 56px;
    background: var(--navbar-bg, var(--bg-surface));
    backdrop-filter: blur(20px) saturate(150%);
    -webkit-backdrop-filter: blur(20px) saturate(150%);
    border-bottom: 1px solid color-mix(in srgb, var(--brand,#15803d) 12%, transparent);
    box-shadow: 0 2px 16px rgba(0,0,0,0.04);
    color: var(--navbar-text, var(--text-primary));
    position: sticky;
    top: 0;
    z-index: 30;
  }
  .menu-toggle {
    display: none;
    border: 1px solid color-mix(in srgb, var(--brand,#15803d) 15%, transparent);
    background: transparent;
    border-radius: 8px;
    padding: 0.35rem 0.55rem;
    font-size: 1.1rem;
    cursor: pointer;
    color: var(--navbar-text, var(--text-primary));
  }
  .topbar-title { flex: 1; min-width: 0; }
  .crumb {
    display: block;
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: color-mix(in srgb, var(--navbar-text, var(--text-primary)) 55%, transparent);
  }
  .topbar-title h1 {
    margin: 0.05rem 0 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--navbar-text, var(--text-primary));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: -0.01em;
  }
  .topbar-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .nav-user {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.3rem 0.6rem;
    background: rgba(0,0,0,0.04);
    border-radius: 10px;
  }
  .user-avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 0.85rem;
    flex-shrink: 0;
    overflow: hidden;
    background: color-mix(in srgb, var(--brand,#15803d) 20%, transparent);
    color: var(--navbar-text, var(--text-primary));
  }
  .avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .user-info {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
  }
  .user-name {
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--navbar-text, var(--text-primary));
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .user-role {
    font-size: 0.6rem;
    color: color-mix(in srgb, var(--navbar-text, var(--text-primary)) 55%, transparent);
    font-weight: 600;
  }
  .logout-btn {
    border: 0;
    background: transparent;
    font-size: 1.1rem;
    cursor: pointer;
    padding: 0.25rem;
    color: color-mix(in srgb, var(--navbar-text, var(--text-primary)) 50%, transparent);
    transition: color .15s;
  }
  .logout-btn:hover { color: #dc2626; }

  .content {
    flex: 1;
    padding: 1rem 1.25rem 1.5rem;
    max-width: 1400px;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
  }

  .app-foot {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.65rem 1.25rem;
    font-size: 0.72rem;
    color: var(--text-secondary);
    border-top: 1px solid var(--border);
    background: var(--bg-surface);
  }
  .app-foot a { color: var(--brand-strong); text-decoration: none; }
  .app-foot a:hover { text-decoration: underline; }

  .backdrop {
    display: none;
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 40%);
    border: 0;
    z-index: 35;
    cursor: pointer;
  }

  @media (max-width: 900px) {
    .sidebar {
      position: fixed;
      left: 0;
      transform: translateX(-100%);
      transition: transform 0.2s ease-out;
    }
    .sidebar.open { transform: translateX(0); }
    .backdrop { display: block; }
    .menu-toggle { display: inline-block; }
    .user-info { display: none; }
    .crumb { display: none; }
  }
</style>
